import type {
  AudioTelemetry,
  CRTVarPatch,
  EngineMode,
  EngineStatus,
  MidiMapping,
  ModulationRoute,
  PadId,
  ParameterId,
  RuntimeState,
  SourceFit,
  SourceKind,
} from "../model/types";
import { AudioEngine } from "./audio-engine";
import { MidiManager, type MidiStatus } from "./midi";
import { ModulationMatrix } from "./modulation";
import { PerformancePads } from "./pads";
import { CanvasRecorder } from "./recording";
import { ScopeRenderer, type ScopeCanvases } from "./scopes";
import { SourceManager } from "./sources";
import {
  DEFAULT_SYNTH_PARAMETERS,
  DEFAULT_VIDEO_PARAMETERS,
  ENGINE_HEIGHT,
  ENGINE_WIDTH,
  clamp01,
  type EngineCapabilities,
  type EngineDiagnostics,
  type RecordingResult,
  type SequencerConfig,
  type SynthParameters,
  type VideoParameters,
} from "./types";
import { WebGLRenderer } from "./webgl-renderer";

export interface EngineCallbacks {
  onStatus?: (status: EngineStatus, message: string) => void;
  onTelemetry?: (telemetry: AudioTelemetry) => void;
  onFps?: (fps: number) => void;
  onSourceStatus?: (status: RuntimeState["sourceStatus"], message: string) => void;
  onRecording?: (recording: boolean, seconds: number, blob?: Blob) => void;
  onMidiStatus?: (status: RuntimeState["midiStatus"]) => void;
  onMidiLearn?: (cc: number, channel: number) => void;
  onError?: (error: Error) => void;
  onContextLoss?: (lost: boolean) => void;
  onRecordingWarning?: (bytes: number) => void;
  onMidiMappingsChange?: (mappings: readonly MidiMapping[]) => void;
}

export interface EngineOptions {
  width?: number;
  height?: number;
  workletUrl?: string;
  safeOutput?: boolean;
  scopes?: ScopeCanvases;
}

const midiStatusForRuntime = (status: MidiStatus): RuntimeState["midiStatus"] => status;

function cutoffToNormalized(hz: number): number {
  const low = 40;
  const high = 18_000;
  return clamp01(Math.log(Math.max(low, Math.min(high, hz)) / low) / Math.log(high / low));
}

function patchSynth(patch: CRTVarPatch): SynthParameters {
  const oscLevelSum = patch.synth.osc1.level + patch.synth.osc2.level;
  return {
    osc1Wave: patch.synth.osc1.waveform,
    osc2Wave: patch.synth.osc2.waveform,
    osc1Octave: Math.max(-3, Math.min(3, Math.round(patch.synth.osc1.octave))),
    osc2Octave: Math.max(-3, Math.min(3, Math.round(patch.synth.osc2.octave))),
    osc1Detune: Math.max(-100, Math.min(100, patch.synth.osc1.detune)),
    osc2Detune: Math.max(-100, Math.min(100, patch.synth.osc2.detune)),
    osc1Level: clamp01(patch.synth.osc1.level),
    osc2Level: clamp01(patch.synth.osc2.level),
    oscMix: oscLevelSum > 0 ? patch.synth.osc2.level / oscLevelSum : 0.5,
    noise: clamp01(patch.synth.noiseLevel),
    attack: Math.max(0.002, Math.min(4, patch.synth.attack)),
    decay: Math.max(0.005, Math.min(4, patch.synth.decay)),
    sustain: clamp01(patch.synth.sustain),
    release: Math.max(0.005, Math.min(8, patch.synth.release)),
    filterCutoff: cutoffToNormalized(patch.synth.cutoff),
    filterResonance: Math.max(0, Math.min(0.98, patch.synth.resonance)),
    drive: clamp01(patch.synth.drive),
    masterGain: 0.55,
    lfo1Rate: Math.max(0.02, Math.min(30, patch.synth.lfoRate)),
    lfo2Rate: Math.max(0.02, Math.min(30, patch.synth.lfoRate * 0.37)),
    lfo1Wave: patch.synth.lfoWaveform,
    lfo2Wave: patch.synth.lfoWaveform,
  };
}

function patchVideo(patch: CRTVarPatch): VideoParameters {
  return {
    horizontalTear: clamp01(patch.video.horizontalJitter * 0.7),
    verticalRoll: Math.max(-1, Math.min(1, patch.video.verticalRoll)),
    lineShift: clamp01(patch.video.horizontalJitter),
    dropout: clamp01(patch.video.horizontalJitter * 0.14),
    chromaDisplacement: clamp01(patch.video.horizontalJitter * 0.32),
    chromaPhase: Math.max(-1, Math.min(1, patch.video.chromaPhase / 180)),
    chromaGain: Math.max(0, Math.min(2, patch.video.chromaGain)),
    desaturation: 0,
    snow: clamp01(patch.video.noise),
    impulses: clamp01(patch.video.noise * 0.35),
    brightnessBars: 0,
    clipping: clamp01(patch.video.clipping),
    solarize: clamp01(patch.video.solarize),
    audioReactive: 0.15,
    feedback: Math.max(0, Math.min(0.96, patch.video.feedback)),
    syncDistortion: Math.max(-1, Math.min(1, patch.video.syncOffset / 8)),
    burstDistortion: clamp01(1 - patch.video.burstAmplitude),
    burstGain: Math.max(0, Math.min(1.5, patch.video.burstAmplitude)),
    linePosition: clamp01(patch.video.linePosition),
    lineWidth: Math.max(0, Math.min(0.35, patch.video.lineWidth)),
    gain: Math.max(0, Math.min(2, patch.video.gain)),
    dcOffset: Math.max(-1, Math.min(1, patch.video.offset)),
  };
}

function capabilities(canvas: HTMLCanvasElement): EngineCapabilities {
  return {
    webgl2: Boolean(canvas.getContext("webgl2")),
    audioWorklet: typeof AudioContext !== "undefined" && "audioWorklet" in AudioContext.prototype,
    sharedArrayBuffer: typeof SharedArrayBuffer !== "undefined" && globalThis.crossOriginIsolated === true,
    camera: typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia),
    midi: typeof navigator !== "undefined" && "requestMIDIAccess" in navigator,
    recording: typeof MediaRecorder !== "undefined" && typeof canvas.captureStream === "function",
  };
}

/**
 * Sole imperative boundary between React and the audio/video runtime.
 * Constructing it starts visual rendering only; Web Audio is created by start().
 */
export class EngineController {
  readonly canvas: HTMLCanvasElement;
  readonly capabilities: EngineCapabilities;

  #callbacks: EngineCallbacks;
  #renderer: WebGLRenderer;
  #audio: AudioEngine;
  #sources: SourceManager;
  #modulation = new ModulationMatrix();
  #pads = new PerformancePads();
  #scopes: ScopeRenderer;
  #midi: MidiManager;
  #recorder: CanvasRecorder;
  #synth: SynthParameters = { ...DEFAULT_SYNTH_PARAMETERS };
  #video: VideoParameters = { ...DEFAULT_VIDEO_PARAMETERS };
  #routes: readonly ModulationRoute[] = [];
  #sequencer?: SequencerConfig;
  #mode: EngineMode = "image";
  #fit: SourceFit = "fit";
  #status: EngineStatus = "locked";
  #statusMessage = "Press START ENGINE to enable audio";
  #bypass = false;
  #bypassBeforePanic = false;
  #raf = 0;
  #disposed = false;
  #lastFrameAt = performance.now();
  #fpsWindowStarted = performance.now();
  #fpsFrames = 0;
  #fps = 0;
  #renderTimes: number[] = [];
  #activeNotes = new Map<string, number>();
  #noteOrder = 0;
  #lastTelemetry: AudioTelemetry = {
    rms: 0, peak: 0, low: 0, mid: 0, high: 0, envelope: 0, sequencerStep: 0, sampleTime: 0,
  };
  #lastError?: string;
  #appliedPatch?: CRTVarPatch;

  constructor(canvas: HTMLCanvasElement, callbacks: EngineCallbacks = {}, options: EngineOptions = {}) {
    this.canvas = canvas;
    this.#callbacks = callbacks;
    this.capabilities = capabilities(canvas);
    this.#renderer = new WebGLRenderer(canvas, options.width ?? ENGINE_WIDTH, options.height ?? ENGINE_HEIGHT, {
      onContextLost: () => {
        this.#callbacks.onContextLoss?.(true);
        this.#setStatus("error", "WebGL context lost; waiting for the browser to restore it");
      },
      onContextRestored: () => {
        this.#callbacks.onContextLoss?.(false);
        this.#setStatus(this.#audio.state === "running" ? "running" : "suspended", "Video renderer restored");
      },
      onError: (error) => this.#reportError(error),
    });
    this.#sources = new SourceManager({
      onChange: (kind) => this.#callbacks.onSourceStatus?.("ready", `${kind} source ready`),
      onError: (kind, error) => {
        const denied = error.name === "NotAllowedError";
        this.#callbacks.onSourceStatus?.(denied ? "denied" : "error", `${kind}: ${error.message}`);
      },
    });
    this.#audio = new AudioEngine(options.workletUrl, 0x43525456, {
      onStateChange: (state) => {
        if (this.#status === "panic" || this.#status === "error") return;
        if (state === "running") this.#setStatus("running", "Audio and video engines running");
        else if (state === "suspended") this.#setStatus("suspended", "Audio suspended by the browser; press Resume");
      },
      onError: (error) => this.#reportError(error),
    });
    this.#scopes = new ScopeRenderer(options.scopes);
    this.#midi = new MidiManager({
      onNoteOn: (owner, note, velocity) => this.noteOn(owner, note, velocity),
      onNoteOff: (owner) => this.noteOff(owner),
      onCc: (target, value) => this.setParameterNormalized(target, value),
      onLearned: (mapping) => {
        this.#callbacks.onMidiLearn?.(mapping.cc, mapping.channel === "all" ? 0 : mapping.channel);
        this.#callbacks.onMidiMappingsChange?.([mapping]);
      },
      onStatus: (status) => this.#callbacks.onMidiStatus?.(midiStatusForRuntime(status)),
      onError: (error) => this.#reportError(error, false),
    });
    this.#recorder = new CanvasRecorder(canvas, () => this.#audio.recordingStream, {
      onProgress: (seconds) => this.#callbacks.onRecording?.(true, seconds),
      onWarning: (bytes) => this.#callbacks.onRecordingWarning?.(bytes),
      onStopped: (result) => this.#callbacks.onRecording?.(false, result.durationMs / 1000, result.blob),
      onError: (error) => this.#reportError(error, false),
    });
    this.#pads.setSafeOutput(options.safeOutput ?? true, true);
    this.#callbacks.onStatus?.(this.#status, this.#statusMessage);
    this.#callbacks.onSourceStatus?.("ready", "Built-in color bars ready");
    this.#raf = requestAnimationFrame(this.#tick);
  }

  get status(): EngineStatus {
    return this.#status;
  }

  get bypass(): boolean {
    return this.#bypass;
  }

  get mode(): EngineMode {
    return this.#mode;
  }

  async start(): Promise<void> {
    if (this.#disposed) throw new Error("This CRTVAR engine has been disposed");
    if (this.#status === "panic") return this.resumeFromPanic();
    this.#setStatus("starting", "Starting audio worklet…");
    try {
      await this.#audio.start();
      this.#audio.setParameters(this.#synth);
      this.#audio.setRoutes(this.#routes);
      if (this.#sequencer) {
        this.#audio.setSequencer(this.#sequencer);
        this.#audio.setTransport(this.#sequencer.enabled);
      }
      this.#setStatus("running", "Audio and video engines running");
    } catch (error) {
      this.#reportError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  async applyPatch(patch: CRTVarPatch): Promise<void> {
    const previous = this.#appliedPatch;
    const changed = <T,>(left: T, right: T): boolean => JSON.stringify(left) !== JSON.stringify(right);
    // Commit the snapshot before any asynchronous source work so rapid UI edits
    // cannot repeatedly reload the same media or reset transport state.
    this.#appliedPatch = structuredClone(patch);

    if (!previous || changed(previous.synth, patch.synth)) {
      this.#synth = patchSynth(patch);
      this.#audio.setParameters(this.#synth);
    }
    if (!previous || changed(previous.video, patch.video)) this.#video = patchVideo(patch);
    if (!previous || previous.video.mode !== patch.video.mode) this.setMode(patch.video.mode);
    if (!previous || previous.source.fit !== patch.source.fit) this.setFit(patch.source.fit);
    if (!previous || changed(previous.modulationRoutes, patch.modulationRoutes)) this.setModulationRoutes(patch.modulationRoutes);
    if (!previous || changed(previous.sequencer, patch.sequencer)) {
      this.setSequencer(patch.sequencer);
    }
    if (!previous || changed(previous.midiMappings, patch.midiMappings)) this.#midi.setMappings(patch.midiMappings);

    if (this.#sources.kind === patch.source.kind) return;
    if (patch.source.kind === "bars" || patch.source.kind === "grid" || patch.source.kind === "scene") {
      await this.setSource(patch.source.kind);
    } else {
      this.#callbacks.onSourceStatus?.("paused", `Relink the local ${patch.source.kind} source for this patch`);
    }
  }

  noteOn(owner: string, note: number, velocity = 1): void {
    if (this.#status === "panic" || this.#status === "locked" || this.#status === "starting") return;
    const previous = this.#activeNotes.get(owner);
    if (previous !== undefined) {
      if (previous === note) return;
      this.#audio.noteOff(previous, owner);
    }
    const normalizedNote = Math.max(0, Math.min(127, Math.round(note)));
    this.#activeNotes.set(owner, normalizedNote);
    this.#noteOrder += 1;
    this.#audio.noteOn(normalizedNote, clamp01(velocity), owner, this.#noteOrder);
  }

  noteOff(owner: string): void {
    const note = this.#activeNotes.get(owner);
    if (note === undefined) return;
    this.#activeNotes.delete(owner);
    this.#audio.noteOff(note, owner);
  }

  allNotesOff(immediate = false): void {
    this.#activeNotes.clear();
    this.#audio.allNotesOff(immediate);
  }

  setPad(pad: PadId, active: boolean): void {
    if (pad === "panic") {
      if (active) this.panic();
      return;
    }
    if (active) this.#pads.press(pad);
    else this.#pads.release(pad);
  }

  panic(): void {
    if (this.#status === "panic") return;
    this.#bypassBeforePanic = this.#bypass;
    this.#pads.press("panic");
    this.#bypass = true;
    this.#renderer.resetTemporalState();
    this.#activeNotes.clear();
    this.#audio.panic();
    this.#audio.setTransport(false, true);
    this.#setStatus("panic", "PANIC active — output muted and video bypassed; press Resume to recover");
  }

  async resumeFromPanic(): Promise<void> {
    this.#pads.resume();
    this.#bypass = this.#bypassBeforePanic;
    await this.#audio.resumeFromPanic();
    this.#setStatus("running", "Recovered from Panic");
  }

  setBypass(enabled: boolean): void {
    if (this.#status === "panic" && !enabled) return;
    this.#bypass = enabled;
  }

  setSafeOutput(enabled: boolean, warningAcknowledged = false): void {
    // The first-party UI performs the confirmation before reaching this boundary.
    this.#pads.setSafeOutput(enabled, warningAcknowledged || !enabled);
  }

  setMode(mode: EngineMode): void {
    this.#mode = mode;
  }

  setFit(fit: SourceFit): void {
    this.#fit = fit;
    this.#sources.setFit(fit);
  }

  setModulationRoutes(routes: readonly ModulationRoute[]): void {
    this.#routes = routes.map((route) => ({ ...route }));
    this.#modulation.setRoutes(this.#routes);
    this.#audio.setRoutes(this.#routes);
  }

  setSequencer(config: SequencerConfig): void {
    this.#sequencer = structuredClone(config);
    this.#audio.setSequencer(config);
    this.#audio.setTransport(config.enabled);
  }

  setTransport(running: boolean, reset = false): void {
    if (this.#status === "panic" && running) return;
    this.#audio.setTransport(running, reset);
  }

  setParameter(id: ParameterId, value: number): void {
    switch (id) {
      case "video.horizontalJitter":
        this.#video.horizontalTear = clamp01(value * 0.7); this.#video.lineShift = clamp01(value); break;
      case "video.verticalRoll": this.#video.verticalRoll = Math.max(-1, Math.min(1, value)); break;
      case "video.chromaPhase": this.#video.chromaPhase = Math.max(-1, Math.min(1, value / 180)); break;
      case "video.chromaGain": this.#video.chromaGain = Math.max(0, Math.min(2, value)); break;
      case "video.noise": this.#video.snow = clamp01(value); this.#video.impulses = clamp01(value * 0.35); break;
      case "video.feedback": this.#video.feedback = Math.max(0, Math.min(0.96, value)); break;
      case "video.burstAmplitude": this.#video.burstGain = Math.max(0, Math.min(1.5, value)); this.#video.burstDistortion = clamp01(1 - value); break;
      case "video.linePosition": this.#video.linePosition = clamp01(value); break;
      case "video.lineWidth": this.#video.lineWidth = Math.max(0, Math.min(0.35, value)); break;
      case "video.gain": this.#video.gain = Math.max(0, Math.min(2, value)); break;
      case "video.offset": this.#video.dcOffset = Math.max(-1, Math.min(1, value)); break;
      case "video.syncOffset": this.#video.syncDistortion = Math.max(-1, Math.min(1, value / 8)); break;
      case "video.clipping": this.#video.clipping = clamp01(value); break;
      case "video.solarize": this.#video.solarize = clamp01(value); break;
      case "synth.osc1Detune": this.#synth.osc1Detune = Math.max(-100, Math.min(100, value)); break;
      case "synth.osc1Level": this.#synth.osc1Level = clamp01(value); break;
      case "synth.osc2Detune": this.#synth.osc2Detune = Math.max(-100, Math.min(100, value)); break;
      case "synth.osc2Level": this.#synth.osc2Level = clamp01(value); break;
      case "synth.noiseLevel": this.#synth.noise = clamp01(value); break;
      case "synth.cutoff": this.#synth.filterCutoff = cutoffToNormalized(value); break;
      case "synth.resonance": this.#synth.filterResonance = Math.max(0, Math.min(0.98, value)); break;
      case "synth.attack": this.#synth.attack = Math.max(0.002, Math.min(4, value)); break;
      case "synth.decay": this.#synth.decay = Math.max(0.005, Math.min(4, value)); break;
      case "synth.sustain": this.#synth.sustain = clamp01(value); break;
      case "synth.release": this.#synth.release = Math.max(0.005, Math.min(8, value)); break;
      case "synth.lfoRate": this.#synth.lfo1Rate = Math.max(0.02, Math.min(30, value)); break;
      case "synth.drive": this.#synth.drive = clamp01(value); break;
    }
    if (id.startsWith("synth.")) this.#audio.setParameters(this.#synth);
  }

  setParameterNormalized(id: ParameterId, normalized: number): void {
    const value = clamp01(normalized);
    const ranges: Record<ParameterId, readonly [number, number, "linear" | "log"]> = {
      "video.horizontalJitter": [0, 1, "linear"], "video.verticalRoll": [-1, 1, "linear"],
      "video.chromaPhase": [-180, 180, "linear"], "video.chromaGain": [0, 2, "linear"],
      "video.noise": [0, 1, "linear"], "video.feedback": [0, 0.96, "linear"],
      "video.burstAmplitude": [0, 1.5, "linear"], "video.linePosition": [0, 1, "linear"],
      "video.lineWidth": [0, 0.35, "linear"], "video.gain": [0, 2, "linear"],
      "video.offset": [-1, 1, "linear"], "video.syncOffset": [-8, 8, "linear"],
      "video.clipping": [0, 1, "linear"], "video.solarize": [0, 1, "linear"],
      "synth.osc1Detune": [-100, 100, "linear"], "synth.osc1Level": [0, 1, "linear"],
      "synth.osc2Detune": [-100, 100, "linear"], "synth.osc2Level": [0, 1, "linear"],
      "synth.noiseLevel": [0, 1, "linear"], "synth.cutoff": [40, 18_000, "log"],
      "synth.resonance": [0, 0.98, "linear"], "synth.attack": [0.002, 4, "log"],
      "synth.decay": [0.005, 4, "log"], "synth.sustain": [0, 1, "linear"],
      "synth.release": [0.005, 8, "log"], "synth.lfoRate": [0.02, 30, "log"],
      "synth.drive": [0, 1, "linear"],
    };
    const [min, max, curve] = ranges[id];
    this.setParameter(id, curve === "log" ? min * (max / min) ** value : min + (max - min) * value);
  }

  async setSource(kind: SourceKind, file?: File, deviceId?: string): Promise<void> {
    this.#callbacks.onSourceStatus?.("loading", `Loading ${kind} source…`);
    await this.#sources.setSource(kind, file, deviceId);
  }

  listCameras(): Promise<readonly MediaDeviceInfo[]> {
    return this.#sources.listCameras();
  }

  startRecording(): void {
    this.#recorder.start(60);
    this.#callbacks.onRecording?.(true, 0);
  }

  async stopRecording(): Promise<Blob> {
    const result = await this.#recorder.stop();
    return result.blob;
  }

  async enableMidi(): Promise<void> {
    await this.#midi.enable();
  }

  setMidiMappings(mappings: readonly MidiMapping[]): void {
    this.#midi.setMappings(mappings);
  }

  beginMidiLearn(target: ParameterId): void {
    this.#midi.beginLearn(target);
  }

  cancelMidiLearn(): void {
    this.#midi.cancelLearn();
  }

  setScopeCanvases(scopes: ScopeCanvases): void {
    this.#scopes.setCanvases(scopes);
  }

  getDiagnostics(): EngineDiagnostics {
    const renderTimes = [...this.#renderTimes].sort((a, b) => a - b);
    const p95 = renderTimes[Math.max(0, Math.ceil(renderTimes.length * 0.95) - 1)] ?? 0;
    return {
      state: this.#status,
      capabilities: this.capabilities,
      renderMode: this.#mode,
      source: this.#sources.kind,
      frame: this.#renderer.frame,
      renderFps: this.#fps,
      renderP95Ms: p95,
      audioSampleFrame: this.#lastTelemetry.sampleTime,
      audioRms: this.#lastTelemetry.rms,
      audioPeak: this.#lastTelemetry.peak,
      activeNotes: this.#activeNotes.size,
      sharedClock: this.#audio.bridge.usesSharedBuffer,
      recordingBytes: this.#recorder.bytes,
      contextLost: this.#renderer.contextLost,
      lastError: this.#lastError,
    };
  }

  #tick = (now: number): void => {
    if (this.#disposed) return;
    const startedAt = performance.now();
    const delta = Math.max(0, Math.min(0.25, (now - this.#lastFrameAt) / 1000));
    this.#lastFrameAt = now;
    const modulation = this.#audio.bridge.read();
    const evaluated = this.#modulation.evaluate(this.#video, this.#synth, modulation, delta);
    const pads = this.#pads.snapshot(now);
    this.#renderer.render({
      now,
      source: this.#sources.getFrame(now),
      fit: this.#fit,
      mode: this.#mode,
      video: evaluated.video,
      pads,
      modulation,
      bypass: this.#bypass || pads.panic,
    });
    const telemetry = this.#scopes.update(now, this.#audio.analyser, this.#renderer, modulation);
    if (telemetry) {
      this.#lastTelemetry = telemetry;
      this.#callbacks.onTelemetry?.(telemetry);
    }
    this.#fpsFrames += 1;
    if (now - this.#fpsWindowStarted >= 500) {
      this.#fps = this.#fpsFrames * 1000 / (now - this.#fpsWindowStarted);
      this.#fpsFrames = 0;
      this.#fpsWindowStarted = now;
      this.#callbacks.onFps?.(this.#fps);
    }
    this.#renderTimes.push(performance.now() - startedAt);
    if (this.#renderTimes.length > 240) this.#renderTimes.shift();
    this.#raf = requestAnimationFrame(this.#tick);
  };

  #setStatus(status: EngineStatus, message: string): void {
    this.#status = status;
    this.#statusMessage = message;
    this.#callbacks.onStatus?.(status, message);
  }

  #reportError(error: Error, fatal = true): void {
    this.#lastError = error.message;
    if (fatal) this.#setStatus("error", error.message);
    this.#callbacks.onError?.(error);
  }

  async dispose(): Promise<void> {
    if (this.#disposed) return;
    this.#disposed = true;
    cancelAnimationFrame(this.#raf);
    this.#recorder.dispose();
    this.#midi.dispose();
    this.#sources.dispose();
    this.#renderer.dispose();
    await this.#audio.close();
  }
}

export type { RecordingResult };
